import { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Grid, MenuItem, Stack, TextField } from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { addCity, editCity, getCityById } from '../../../../api/endpoints/cmsContent';
import type { City } from '../../../../api/types/cmsContent';
import { ApiErrorAlert } from '../../../../components/ApiErrorAlert';
import { useFreshEditRow } from '../../../../components/useFreshEditRow';

const schema = z.object({
  name: z.string().min(1, 'Şehir adı gerekli'),
  displayName: z.string().optional(),
  content: z.string().optional(),
  keywords: z.string().optional(),
  description: z.string().optional(),
  imagePath: z.string().optional(),
  type: z.string().optional(),
});

type FormValues = z.infer<typeof schema>;

const emptyValues: FormValues = {
  name: '',
  displayName: '',
  content: '',
  keywords: '',
  description: '',
  imagePath: '',
  type: 'DOM',
};

function toFormValues(city: City): FormValues {
  return {
    name: city.name ?? '',
    displayName: city.displayName ?? '',
    content: city.content ?? '',
    keywords: city.keywords ?? '',
    description: city.description ?? '',
    imagePath: city.imagePath ?? '',
    type: city.type ?? 'DOM',
  };
}

/** Create/edit dialog for Şehirler (City). Confirmed via local-backend swagger + full live
 *  curl round-trip (see api/types/cmsContent.ts header). `type` is offered as a free-editable
 *  select seeded with the only 2 values observed on real rows ("DOM"/"INT") — not a
 *  swagger-confirmed enum, see that file's `City` doc comment. Always rendered inside a
 *  specific site's tab, so `site_Id` is injected from the `siteId` prop. */
export function CityFormDialog({
  open,
  city,
  siteId,
  onClose,
}: {
  open: boolean;
  /** null = create mode; otherwise edit mode for this row. */
  city: City | null;
  siteId: number;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const isEdit = !!city;

  const {
    control,
    handleSubmit,
    register,
    reset,
    formState: { errors },
  } = useForm<FormValues>({ resolver: zodResolver(schema), defaultValues: emptyValues });

  // Refetch the fresh row on edit-open; silently falls back to the list row on error.
  const seed = useFreshEditRow({
    open,
    editing: city,
    queryKey: ['cms', 'content', 'city', city?.id],
    fetch: async (row) => (await getCityById(row.id)).cities?.[0],
  });

  useEffect(() => {
    if (open) reset(seed ? toFormValues(seed) : emptyValues);
  }, [open, seed, reset]);

  const mutation = useMutation({
    mutationFn: (values: FormValues) =>
      isEdit && city
        ? editCity({ id: city.id, site_Id: siteId, ...values })
        : addCity({ ...values, site_Id: siteId }),
    onSuccess: () => {
      enqueueSnackbar(isEdit ? 'Şehir güncellendi.' : 'Şehir oluşturuldu.', { variant: 'success' });
      queryClient.invalidateQueries({ queryKey: ['cms', 'content', 'cities'] });
      onClose();
    },
    onError: () => {
      /* surfaced via ApiErrorAlert below */
    },
  });

  const submit = handleSubmit((values) => mutation.mutate(values));

  return (
    <Dialog open={open} onClose={() => !mutation.isPending && onClose()} maxWidth="sm" fullWidth>
      <DialogTitle>{isEdit ? 'Şehri Güncelle' : 'Yeni Şehir'}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                size="small"
                label="Ad"
                error={!!errors.name}
                helperText={errors.name?.message}
                {...register('name')}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField fullWidth size="small" label="Görünen Ad" {...register('displayName')} />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Controller
                control={control}
                name="type"
                render={({ field }) => (
                  <TextField {...field} select fullWidth size="small" label="Tip">
                    <MenuItem value="DOM">DOM</MenuItem>
                    <MenuItem value="INT">INT</MenuItem>
                  </TextField>
                )}
              />
            </Grid>
            <Grid size={12}>
              <TextField fullWidth size="small" label="Görsel Yolu" {...register('imagePath')} />
            </Grid>
            <Grid size={12}>
              <TextField fullWidth size="small" label="Anahtar Kelimeler" {...register('keywords')} />
            </Grid>
            <Grid size={12}>
              <TextField fullWidth size="small" multiline rows={2} label="Açıklama" {...register('description')} />
            </Grid>
            <Grid size={12}>
              <TextField fullWidth size="small" multiline rows={3} label="İçerik" {...register('content')} />
            </Grid>
          </Grid>
          <ApiErrorAlert error={mutation.error} />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={mutation.isPending}>
          Vazgeç
        </Button>
        <Button onClick={submit} variant="contained" disabled={mutation.isPending}>
          {mutation.isPending ? 'Kaydediliyor…' : 'Kaydet'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
